// =============================================================
// InternshipProjectPage.js
// -------------------------------------------------------------
// Combined Internship + Project management page. The two
// resources share identical shape and behaviour (the spec asks
// for the same UI twice), so we build ONE page component that
// takes a `module` prop ('internship' | 'project') and
// configures everything — endpoints, labels, status enums,
// form fields, KPI cards, analytics endpoints — from a single
// config table. Keeps the code in lock-step with the backend
// model definitions.
//
// Tabs:
//   • Enquiry Management  — list + add/edit/delete + convert
//   • Joined Management   — list + add/edit/delete + certificate
//   • Analytics View      — toggle to show analytics above the table
// =============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import StatCard from '../../../components/common/StatCard';
import AlertToast from '../../../components/common/AlertToast';
import ViewToggle, { readViewPreference } from '../../../components/common/ViewToggle';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import AnimatedKpiCard from '../../../components/common/AnimatedKpiCard';
import ChartCard from '../../../components/charts/ChartCard';
import { BarChart, DoughnutChart, LineChart, EmptyChart } from '../../../components/charts/AnalyticsCharts';
import JoinedResourceCollectionsAnalytics from '../../../components/analytics/JoinedResourceCollectionsAnalytics';
import { AnalyticsToolbar, AnalyticsSettingsModal, AnalyticsInfoModal, useAnalyticsControls } from '../../../components/analytics/AnalyticsControls';
import CourseToggleTrendCard from '../../../components/analytics/CourseToggleTrendCard';

// ─── Module-specific config ─────────────────────────────────────────────────
// Single source of truth for everything that differs between the
// internship and project modules. Adding a new field or status
// happens here once, and both the table + form + analytics pick it up.

const MODULE_CONFIG = {
  internship: {
    title: 'Internships',
    singular: 'Internship',
    pagePath: '/internships',
    enq: {
      singular: 'Internship Enquiry',
      apiBase: '/training/internships/enquiries',
      analyticsBase: '/training/analytics/internships/enquiries',
      resource: 'internship',
      pagePath: '/internships',
      numberLabel: 'Enquiry ID',
      numberPrefix: 'INT-E-',
      typeLabel: 'Internship Type',
      badgeColor: '#3b82f6',
    },
    joined: {
      singular: 'Joined Internship',
      apiBase: '/training/internships/joined',
      analyticsBase: '/training/analytics/internships/joined',
      resource: 'joined-internship',
      pagePath: '/internships',
      numberLabel: 'Joined ID',
      numberPrefix: 'INT-J-',
      typeLabel: 'Internship Type',
      badgeColor: '#22c55e',
    },
  },
  project: {
    title: 'Projects',
    singular: 'Project',
    pagePath: '/projects',
    enq: {
      singular: 'Project Enquiry',
      apiBase: '/training/projects/enquiries',
      analyticsBase: '/training/analytics/projects/enquiries',
      resource: 'project',
      pagePath: '/projects',
      numberLabel: 'Enquiry ID',
      numberPrefix: 'PRJ-E-',
      typeLabel: 'Project Type',
      badgeColor: '#8b5cf6',
    },
    joined: {
      singular: 'Joined Project',
      apiBase: '/training/projects/joined',
      analyticsBase: '/training/analytics/projects/joined',
      resource: 'joined-project',
      pagePath: '/projects',
      numberLabel: 'Joined ID',
      numberPrefix: 'PRJ-J-',
      typeLabel: 'Project Type',
      badgeColor: '#a855f7',
    },
  },
};

// ─── Local enum constants ───────────────────────────────────────────────────
const TYPES = [
  { value: 'individual', label: 'Individual', color: 'primary', icon: 'bi-person-fill' },
  { value: 'group',      label: 'Group',      color: 'info',    icon: 'bi-people-fill' },
];

const ENQ_STATUSES = [
  { value: 'new',        label: 'New',         color: 'primary' },
  { value: 'contacted',  label: 'Contacted',   color: 'info' },
  { value: 'follow_up',  label: 'Follow Up',   color: 'warning' },
  { value: 'converted',  label: 'Converted',   color: 'success' },
  { value: 'lost',       label: 'Lost',        color: 'secondary' },
];

const JOINED_STATUSES = [
  { value: 'ongoing',    label: 'Ongoing',    color: 'primary' },
  { value: 'completed',  label: 'Completed',  color: 'success' },
  { value: 'cancelled',  label: 'Cancelled',  color: 'danger' },
];

// Helper lookups
const enumMeta = (arr, v) => arr.find(x => x.value === v) || { label: v || '—', color: 'secondary' };
const fmtINR = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
}).format(Number(n) || 0);
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN');
};

// ─── Reusable Bootstrap modal ───────────────────────────────────────────────
// Same inline modal as every other training page — kept here so we
// don't have to import it from a shared file (which would couple
// this module to a UI primitive outside its scope).
const BSModal = ({ show, onClose, title, size = '', children, footer }) =>
  show ? (
    <div
      className="modal fade show d-block"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`modal-dialog modal-dialog-scrollable modal-dialog-centered ${size}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null;

// ─── Generic table component (shared between Enquiry & Joined tabs) ────────
// Reused twice — once per tab. Pass the resource config + the
// current records and it renders the table, pagination, and
// row action buttons uniformly.
const ResourceTable = ({
  records, totalPages, page, loading, search, filterStatus, filterType,
  statuses, types, hasMembers, hasCertificate, config,
  canWrite, canAdmin, isJoined,
  hasAmount, onAddPayment,
  onPageChange, onSearchChange, onFilterStatusChange, onFilterTypeChange,
  onAdd, onEdit, onDelete, onStatusChange, onCertificateToggle, onConvert, onFollowUp, onViewDetails,
}) => (
  <>
    {/* Filter bar */}
    <div className="row g-2 mb-3">
      <div className="col-md-4">
        <div className="input-group input-group-sm">
          <span className="input-group-text"><i className="bi bi-search" /></span>
          <input
            className="form-control"
            placeholder={`Search by name, phone, college…`}
            value={search}
            onChange={e => { onSearchChange(e.target.value); onPageChange(1); }}
          />
        </div>
      </div>
      <div className="col-md-3">
        <select className="form-select form-select-sm" value={filterStatus}
          onChange={e => { onFilterStatusChange(e.target.value); onPageChange(1); }}>
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      <div className="col-md-3">
        <select className="form-select form-select-sm" value={filterType}
          onChange={e => { onFilterTypeChange(e.target.value); onPageChange(1); }}>
          <option value="">All Types</option>
          {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div className="col-md-2 d-flex align-items-center justify-content-end">
        <small className="text-muted">
          {loading ? 'Loading…' : `${records.length} record${records.length === 1 ? '' : 's'}`}
        </small>
      </div>
    </div>

    {/* Table */}
    <div className="card shadow-sm">
      <div style={{ width: '100%' }}>
        <table className="table table-sm table-hover align-middle mb-0" style={{ tableLayout: 'auto', width: '100%' }}>
          <thead className="table-light">
            <tr>
              <th style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>{config.numberLabel}</th>
              <th style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>Date</th>
              <th style={{ padding: '5px 6px' }}>Name</th>
              <th style={{ padding: '5px 6px' }}>College</th>
              <th style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>Phone</th>
              <th style={{ padding: '5px 6px' }}>Course</th>
              <th style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>{config.typeLabel}</th>
              {hasMembers && <th className="text-center" style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>M</th>}
              {hasAmount && <th style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>Amount</th>}
              {hasCertificate && <th className="text-center" style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>Cert.</th>}
              <th style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>Status</th>
              <th className="text-end" style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && !loading && (
              <tr><td colSpan={hasAmount ? 12 : 11} className="text-center text-muted py-4">
                <i className="bi bi-inbox" style={{ fontSize: 24 }} />
                <div className="mt-2">No records yet — click "New" to add one.</div>
              </td></tr>
            )}
            {records.map(r => {
              const sm   = enumMeta(statuses, r.status);
              const tm   = enumMeta(types,   r.type);
              return (
                <tr key={r._id}>
                  <td style={{ padding: '5px 6px' }}>
                    <small className="font-monospace text-muted">
                      {r.enquiryNumber || r.joinedNumber || '—'}
                    </small>
                  </td>
                  <td style={{ padding: '5px 6px' }}>
                    <small style={{ fontSize: 12 }}>{fmtDate(r.enquiryDate)}</small>
                  </td>
                  <td style={{ padding: '5px 6px' }}>
                    <div className="fw-semibold text-truncate" style={{ fontSize: 12 }} title={r.studentName}>{r.studentName}</div>
                    {r.email && <small className="text-muted d-block text-truncate" style={{ fontSize: 11 }} title={r.email}>{r.email}</small>}
                  </td>
                  <td style={{ padding: '5px 6px' }}><small className="d-block text-truncate" style={{ fontSize: 11 }} title={r.collegeName || '—'}>{r.collegeName || '—'}</small></td>
                  <td style={{ padding: '5px 6px' }}><small style={{ fontSize: 11 }}>{r.phoneNumber || '—'}</small></td>
                  <td style={{ padding: '5px 6px' }}>
                    <small className="d-block text-truncate" style={{ fontSize: 11 }} title={r.courseName || r.otherCourse || '—'}>{r.courseName || r.otherCourse || '—'}</small>
                  </td>
                  <td style={{ padding: '5px 6px' }}>
                    <span className={`badge bg-${tm.color} text-white fw-semibold`} style={{ fontSize: 10, padding: '2px 6px' }}>
                      <i className={`bi ${tm.icon || 'bi-circle'} me-1`} />
                      {tm.label}
                    </span>
                  </td>
                  {hasMembers && (
                    <td className="text-center" style={{ padding: '5px 6px' }}>
                      <span className="badge bg-light text-dark" style={{ fontSize: 12 }}>{r.membersCount}</span>
                    </td>
                  )}
                  {hasAmount && (
                    <td style={{ padding: '5px 6px' }}>
                      <div className="fw-semibold" style={{ fontSize: 12 }}>{fmtINR(r.totalAmount)}</div>
                      <small className="text-muted d-block" style={{ fontSize: 11 }}>
                        {fmtINR(r.amountPerPerson)} / person
                      </small>
                      {isJoined && (
                        <small className="text-muted d-block" style={{ fontSize: 11 }}>
                          Paid: {fmtINR(r.paidAmount || 0)} | Bal: {fmtINR(r.balanceAmount || 0)}
                        </small>
                      )}
                    </td>
                  )}
                  {hasCertificate && (
                    <td className="text-center" style={{ padding: '5px 6px' }}>
                      {(r.certificateGiven || r.certificateIssued)
                        ? <span className="badge bg-success" style={{ fontSize: 10, padding: '2px 6px' }}><i className="bi bi-patch-check-fill me-1" />Issued</span>
                        : <span className="badge bg-light text-muted" style={{ fontSize: 10, padding: '2px 6px' }}>Not yet</span>}
                    </td>
                  )}
                  <td style={{ padding: '5px 6px' }}>
                    <span className={`badge bg-${sm.color} text-white`} style={{ fontSize: 10, padding: '2px 6px' }}>{sm.label}</span>
                  </td>
                  <td className="text-end" style={{ padding: '5px 6px' }}>
                    {!isJoined && canWrite && (
                      <button
                        className={`btn btn-sm me-1 py-0 px-2 ${r.status === 'converted' ? 'btn-success' : 'btn-outline-secondary'}`}
                        style={{ fontSize: 12 }}
                        title={r.status === 'converted' ? 'Already added to joined tab' : 'Add this enquiry to joined tab'}
                        onClick={() => {
                          if (r.status === 'converted') return;
                          onConvert && onConvert(r);
                        }}
                        disabled={r.status === 'converted'}
                      >
                        <i className={`bi ${r.status === 'converted' ? 'bi-check-circle' : 'bi-plus-circle'} me-1`} />
                        {r.status === 'converted' ? 'Joined' : 'Add to Joined'}
                      </button>
                    )}
                    {!isJoined && canWrite && onFollowUp && (
                      <button className="btn btn-outline-info btn-sm me-1 py-0 px-2" style={{ fontSize: 12 }} title="Follow up conversation" onClick={() => onFollowUp(r)}>
                        <i className="bi bi-chat-dots" />
                      </button>
                    )}
                    {canWrite && onStatusChange && (
                      <select
                        className="form-select form-select-sm d-inline-block me-1 py-0"
                        value={r.status}
                        onChange={e => onStatusChange(r, e.target.value)}
                        style={{ minWidth: 82, fontSize: 11 }}
                      >
                        {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    )}
                    {hasCertificate && canWrite && (
                      <button
                        className={`btn btn-sm me-1 py-0 px-2 ${r.certificateGiven ? 'btn-success' : 'btn-outline-success'}`}
                        style={{ fontSize: 12 }}
                        title={r.certificateGiven ? 'Certificate issued' : 'Mark certificate issued'}
                        onClick={() => onCertificateToggle(r)}
                      >
                        <i className="bi bi-patch-check" />
                      </button>
                    )}
                    {isJoined && canWrite && onAddPayment && (
                      <button className="btn btn-outline-warning btn-sm me-1" title="Add payment" onClick={() => onAddPayment(r)}>
                        <i className="bi bi-cash-coin" />
                      </button>
                    )}
                    {onViewDetails && (
                      <button className="btn btn-outline-secondary btn-sm me-1" title="View details" onClick={() => onViewDetails(r)}>
                        <i className="bi bi-eye" />
                      </button>
                    )}
                    {canWrite && (
                      <button className="btn btn-outline-primary btn-sm me-1" title="Edit"
                        onClick={() => onEdit(r)}>
                        <i className="bi bi-pencil" />
                      </button>
                    )}
                    {canAdmin && (
                      <button className="btn btn-outline-danger btn-sm" title="Delete"
                        onClick={() => onDelete(r)}>
                        <i className="bi bi-trash" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="mt-3 mb-0 d-flex justify-content-center">
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(Math.max(1, page - 1))}>‹</button>
            </li>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                <button className="page-link" onClick={() => onPageChange(p)}>{p}</button>
              </li>
            ))}
            <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(Math.min(totalPages, page + 1))}>›</button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  </>
);

// ─── Resource form modal (used for both Enquiry and Joined) ─────────────────
// One form for both resources — the field set differs only by
// whether the resource is an Enquiry or a Joined record, and
// whether the joined record has lifecycle fields (joiningDate,
// status, certificateGiven).
const ResourceForm = ({
  show, onClose, form, setForm, isEdit, isJoined,
  courses, saving, error, onSubmit, config,
}) => {
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCourseChange = (e) => {
    const v = e.target.value;
    if (v === '__other__') {
      setForm(p => ({ ...p, courseId: '', courseName: '', courseOther: true }));
    } else {
      const c = courses.find(x => x._id === v);
      setForm(p => ({ ...p, courseId: v, courseName: c?.courseName || '', courseOther: false }));
    }
  };

  return (
    <BSModal show={show} onClose={onClose} title={isEdit ? `Edit ${config.singular}` : `New ${config.singular}`} size="lg"
      footer={
        <>
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={onSubmit} disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : isEdit ? 'Update' : 'Create'}
          </button>
        </>
      }>
      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Student Name <span className="text-danger">*</span></label>
          <input className="form-control form-control-sm" value={form.studentName || ''}
            onChange={e => setF('studentName', e.target.value)} placeholder="e.g. Arun Kumar" />
        </div>
        <div className="col-md-6">
          <label className="form-label">Enquiry Date</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={form.enquiryDate ? new Date(form.enquiryDate).toISOString().split('T')[0] : ''}
            onChange={e => setF('enquiryDate', e.target.value)}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">College Name</label>
          <input className="form-control form-control-sm" value={form.collegeName || ''}
            onChange={e => setF('collegeName', e.target.value)} placeholder="e.g. ABC Engineering College" />
        </div>
        <div className="col-md-4">
          <label className="form-label">Phone Number</label>
          <input className="form-control form-control-sm" value={form.phoneNumber || ''}
            onChange={e => setF('phoneNumber', e.target.value)} placeholder="9876543210" />
        </div>
        <div className="col-md-4">
          <label className="form-label">{config.typeLabel}</label>
          <select className="form-select form-select-sm" value={form.type || 'individual'}
            onChange={e => setF('type', e.target.value)}>
            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label">Members</label>
          <input type="number" min="1" className="form-control form-control-sm" value={form.membersCount || 1}
            onChange={e => setF('membersCount', parseInt(e.target.value, 10) || 1)} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Amount / Person</label>
          <input type="number" min="0" className="form-control form-control-sm" value={form.amountPerPerson || 0}
            onChange={e => setF('amountPerPerson', Math.max(0, Number(e.target.value || 0)))} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Total Amount</label>
          <input className="form-control form-control-sm" value={fmtINR((Number(form.amountPerPerson || 0) || 0) * (Number(form.membersCount || 1) || 1))} readOnly />
        </div>

        <div className="col-md-8">
          <label className="form-label">Course</label>
          <select className="form-select form-select-sm"
            value={form.courseOther ? '__other__' : (form.courseId || '')}
            onChange={handleCourseChange}>
            <option value="">Select Course</option>
            {courses.map(c => <option key={c._id} value={c._id}>{c.courseName}</option>)}
            <option value="__other__">Other (specify below)</option>
          </select>
        </div>
        {form.courseOther && (
          <div className="col-md-4">
            <label className="form-label">Other Course</label>
            <input className="form-control form-control-sm" value={form.otherCourse || ''}
              onChange={e => setF('otherCourse', e.target.value)} placeholder="specify course" />
          </div>
        )}

        {/* Joined-only lifecycle fields */}
        {isJoined && (
          <>
          <div className="col-md-4">
            <label className="form-label">Joining Date</label>
              <input type="date" className="form-control form-control-sm"
                value={form.joiningDate ? new Date(form.joiningDate).toISOString().split('T')[0] : ''}
                onChange={e => setF('joiningDate', e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Completed Date</label>
              <input type="date" className="form-control form-control-sm"
                value={form.completedDate ? new Date(form.completedDate).toISOString().split('T')[0] : ''}
                onChange={e => setF('completedDate', e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Status</label>
              <select className="form-select form-select-sm" value={form.status || 'ongoing'}
                onChange={e => setF('status', e.target.value)}>
                {JOINED_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <div className="form-check form-switch mt-4">
                <input className="form-check-input" type="checkbox" id="certGiven"
                  checked={!!form.certificateGiven} onChange={e => setF('certificateGiven', e.target.checked)} />
                <label className="form-check-label" htmlFor="certGiven">Certificate Given</label>
              </div>
            </div>
            <div className="col-md-8">
              <label className="form-label">Certificate Number</label>
              <input className="form-control form-control-sm" value={form.certificateNumber || ''}
                onChange={e => setF('certificateNumber', e.target.value)} placeholder="optional" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Collected Amount</label>
              <input className="form-control form-control-sm" value={fmtINR(form.paidAmount || 0)} readOnly />
            </div>
            <div className="col-md-4">
              <label className="form-label">Balance Amount</label>
              <input className="form-control form-control-sm" value={fmtINR(Math.max(0, (Number(form.amountPerPerson || 0) * Number(form.membersCount || 1)) - Number(form.paidAmount || 0)))} readOnly />
            </div>
          </>
        )}

        <div className="col-12">
          <label className="form-label">Notes</label>
          <textarea className="form-control form-control-sm" rows="2" value={form.notes || ''}
            onChange={e => setF('notes', e.target.value)} placeholder="Any additional notes…" />
        </div>
      </div>
    </BSModal>
  );
};

const FollowUpModal = ({
  show, onClose, record, form, setForm, saving, onSubmit,
}) => (
  <BSModal
    show={show}
    onClose={onClose}
    title={`Follow Up${record?.studentName ? ` - ${record.studentName}` : ''}`}
    footer={(
      <>
        <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={onSubmit} disabled={saving}>
          {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving...</> : 'Save Follow Up'}
        </button>
      </>
    )}
  >
    <div className="row g-3">
      <div className="col-md-4">
        <label className="form-label">Method</label>
        <select className="form-select form-select-sm" value={form.method || 'call'} onChange={e => setForm((p) => ({ ...p, method: e.target.value }))}>
          <option value="call">Call</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="email">Email</option>
          <option value="visit">Visit</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Outcome</label>
        <select className="form-select form-select-sm" value={form.outcome || 'callback'} onChange={e => setForm((p) => ({ ...p, outcome: e.target.value }))}>
          <option value="callback">Callback</option>
          <option value="interested">Interested</option>
          <option value="not_interested">Not Interested</option>
          <option value="joined">Joined</option>
          <option value="closed">Closed</option>
        </select>
      </div>
      <div className="col-md-4">
        <label className="form-label">Next Follow Up</label>
        <input type="date" className="form-control form-control-sm" value={form.nextFollowUp || ''} onChange={e => setForm((p) => ({ ...p, nextFollowUp: e.target.value }))} />
      </div>
      <div className="col-12">
        <label className="form-label">Conversation</label>
        <textarea className="form-control form-control-sm" rows="3" value={form.conversation || ''} onChange={e => setForm((p) => ({ ...p, conversation: e.target.value }))} placeholder="Discussed requirement, budget, timing, next step..." />
      </div>
      <div className="col-12">
        <div className="border rounded p-2" style={{ maxHeight: 260, overflowY: 'auto', background: '#f8fafc' }}>
          {(record?.followUps || []).length ? (
            [...record.followUps].slice().reverse().map((item) => (
              <div key={item._id || `${item.date}-${item.conversation}`} className="border-bottom pb-2 mb-2 last-border-0">
                <div className="d-flex justify-content-between gap-2">
                  <div className="small fw-semibold text-dark">
                    {(item.method || 'call').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    {item.outcome ? ` - ${String(item.outcome).replace(/_/g, ' ')}` : ''}
                  </div>
                  <div className="small text-muted">{fmtDate(item.date)}</div>
                </div>
                <div className="small text-muted">{item.conductedBy?.firstName ? `${item.conductedBy.firstName} ${item.conductedBy.lastName || ''}`.trim() : 'User'}</div>
                <div className="small mt-1">{item.conversation || '-'}</div>
                {item.nextFollowUp && <div className="small text-primary mt-1">Next: {fmtDate(item.nextFollowUp)}</div>}
              </div>
            ))
          ) : (
            <div className="small text-muted">No follow-up conversation added yet.</div>
          )}
        </div>
      </div>
    </div>
  </BSModal>
);

const ResourceDetailsModal = ({
  show, onClose, record, isJoined, onEdit,
}) => (
  <BSModal
    show={show}
    onClose={onClose}
    title={`${isJoined ? 'Record' : 'Enquiry'} Details${record?.studentName ? ` - ${record.studentName}` : ''}`}
    size="lg"
    footer={(
      <>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        {record && <button className="btn btn-primary btn-sm" onClick={() => onEdit && onEdit(record)}>Edit</button>}
      </>
    )}
  >
    {record && (
      <div className="row g-3">
        <div className="col-md-6"><div className="small text-muted">Name</div><div className="fw-semibold">{record.studentName || '-'}</div></div>
        <div className="col-md-3"><div className="small text-muted">Date</div><div>{fmtDate(record.enquiryDate)}</div></div>
        <div className="col-md-3"><div className="small text-muted">Status</div><div>{record.status || '-'}</div></div>
        <div className="col-md-6"><div className="small text-muted">College</div><div>{record.collegeName || '-'}</div></div>
        <div className="col-md-6"><div className="small text-muted">Phone</div><div>{record.phoneNumber || '-'}</div></div>
        <div className="col-md-4"><div className="small text-muted">Type</div><div>{record.type || '-'}</div></div>
        <div className="col-md-4"><div className="small text-muted">Members</div><div>{record.membersCount || 1}</div></div>
        <div className="col-md-4"><div className="small text-muted">Amount / Person</div><div>{fmtINR(record.amountPerPerson || 0)}</div></div>
        <div className="col-md-6"><div className="small text-muted">Course</div><div>{record.courseName || record.otherCourse || '-'}</div></div>
        <div className="col-md-6"><div className="small text-muted">Total Amount</div><div>{fmtINR(record.totalAmount || 0)}</div></div>
        {isJoined && (
          <>
            <div className="col-md-4"><div className="small text-muted">Joining Date</div><div>{fmtDate(record.joiningDate)}</div></div>
            <div className="col-md-4"><div className="small text-muted">Completed Date</div><div>{fmtDate(record.completedDate)}</div></div>
            <div className="col-md-4"><div className="small text-muted">Certificate</div><div>{record.certificateGiven ? 'Issued' : 'Not issued'}</div></div>
            <div className="col-md-4"><div className="small text-muted">Collected</div><div>{fmtINR(record.paidAmount || 0)}</div></div>
            <div className="col-md-4"><div className="small text-muted">Balance</div><div>{fmtINR(record.balanceAmount || 0)}</div></div>
            <div className="col-md-4"><div className="small text-muted">Certificate No</div><div>{record.certificateNumber || '-'}</div></div>
          </>
        )}
        {!isJoined && !!record.nextFollowUp && (
          <div className="col-md-6"><div className="small text-muted">Next Follow Up</div><div>{fmtDate(record.nextFollowUp)}</div></div>
        )}
        <div className="col-12"><div className="small text-muted">Notes</div><div className="border rounded p-2 bg-light">{record.notes || '-'}</div></div>
        {!isJoined && Array.isArray(record.followUps) && record.followUps.length > 0 && (
          <div className="col-12">
            <div className="small text-muted mb-2">Follow Up Conversation</div>
            <div className="border rounded p-2" style={{ maxHeight: 240, overflowY: 'auto', background: '#f8fafc' }}>
              {[...record.followUps].slice().reverse().map((item) => (
                <div key={item._id || `${item.date}-${item.conversation}`} className="border-bottom pb-2 mb-2">
                  <div className="d-flex justify-content-between gap-2">
                    <div className="small fw-semibold">{item.method || 'call'}{item.outcome ? ` - ${item.outcome}` : ''}</div>
                    <div className="small text-muted">{fmtDate(item.date)}</div>
                  </div>
                  <div className="small mt-1">{item.conversation || '-'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </BSModal>
);

// ─── Analytics panel for a single resource (enquiry OR joined) ─────────────
// Fetches /api/training/analytics/{internships|projects}/{enquiries|joined}
// and renders KPI cards + bar/donut/line charts. Reused by both
// module pages.
const ResourceAnalytics = ({ analyticsBase, accentColor, isJoined, navigateTo }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const controls = useAnalyticsControls({
    storageKey: `resource-analytics:${analyticsBase}`,
    chartKeys: ['trend', 'status', 'college', 'course', 'type', 'courseTrend'],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(analyticsBase, { params: controls.queryParams });
      setData(r.data?.data || {});
    } catch (_) { setData({}); }
    finally { setLoading(false); }
  }, [analyticsBase, controls.queryParams]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <LoadingSpinner />;
  if (!data) return null;

  const kpis         = data.kpis || {};
  const monthlyTrend = data.monthlyTrend || [];
  const statusSlices = data.statusSlices || [];
  const byCollege    = data.byCollege || [];
  const byCourse     = data.byCourse || [];
  const courseTrendSeries = data.courseTrendSeries || [];
  const byType       = data.byType || [];
  const meta         = data.meta || {};
  const visible      = controls.state.chartVisibility || {};
  const chartOptions = [
    { key: 'trend', label: 'Trend', help: 'Creation trend for the selected period' },
    { key: 'status', label: 'Status', help: 'Status distribution' },
    { key: 'college', label: 'By College', help: 'Top colleges' },
    { key: 'course', label: 'By Course', help: 'Top courses' },
    { key: 'type', label: 'Individual vs Group', help: 'Type distribution' },
    { key: 'courseTrend', label: 'Course Line Comparison', help: 'Toggle individual course lines' },
  ];

  // KPI strip — different cards for enquiry vs joined
  const kpiCards = isJoined ? [
    { icon: 'bi-briefcase-fill',     label: 'Total',          value: kpis.total,           color: accentColor },
    { icon: 'bi-play-circle-fill',   label: 'Ongoing',        value: kpis.total - (data.statusSlices.find(s => s.label === 'completed')?.value || 0) - (data.statusSlices.find(s => s.label === 'cancelled')?.value || 0), color: '#3b82f6' },
    { icon: 'bi-check-circle-fill',  label: 'Completed',      value: data.statusSlices.find(s => s.label === 'completed')?.value || 0, color: '#22c55e' },
    { icon: 'bi-patch-check-fill',   label: 'Certificates',   value: kpis.certificateGiven || 0, color: '#8b5cf6' },
  ] : [
    { icon: 'bi-chat-square-text-fill', label: 'Total Enquiries', value: kpis.total,        color: accentColor },
    { icon: 'bi-stars',                 label: 'New This Month',  value: kpis.newThisMonth, color: '#3b82f6' },
    { icon: 'bi-check-circle-fill',     label: 'Converted',       value: data.statusSlices.find(s => s.label === 'converted')?.value || 0, color: '#22c55e' },
    { icon: 'bi-x-circle-fill',         label: 'Lost',            value: data.statusSlices.find(s => s.label === 'lost')?.value || 0, color: '#94a3b8' },
  ];

  return (
    <div>
      <AnalyticsToolbar
        title={isJoined ? 'Joined Analytics' : 'Enquiry Analytics'}
        subtitle="Adjust date range, grouping, and chart visibility."
        state={controls.state}
        meta={meta}
        onPresetChange={controls.setPreset}
        onGroupByChange={controls.setGroupBy}
        onFromChange={controls.setFrom}
        onToChange={controls.setTo}
        onOpenSettings={() => setShowSettings(true)}
        onOpenInfo={() => setShowInfo(true)}
      />
      {visible.courseTrend !== false && (
        <div className="row g-3 mb-4">
          <div className="col-12">
            <CourseToggleTrendCard
              title="Course Comparison Trend"
              subtitle={meta.periodLabel || 'Compare records by course'}
              iconColor={accentColor}
              series={courseTrendSeries}
              onClick={() => navigateTo && navigateTo()}
            />
          </div>
        </div>
      )}
      <div className="row g-3 mb-4">
        {kpiCards.map((k, i) => (
          <div key={i} className="col-6 col-md-3">
            <AnimatedKpiCard icon={k.icon} label={k.label} value={k.value} color={k.color} loading={loading} />
          </div>
        ))}
      </div>

      <div className="row g-3 mb-4">
        {visible.trend !== false && <div className="col-lg-7">
          <ChartCard
            title="Monthly Trend"
            subtitle={meta.periodLabel || 'Trend for the selected range'}
            icon="bi-graph-up-arrow" iconColor={accentColor}
            onClick={() => navigateTo && navigateTo()}
          >
            {monthlyTrend.some(m => m.count > 0) ? (
              <LineChart
                height={220}
                labels={monthlyTrend.map(m => m.label)}
                datasets={[{ label: 'New', data: monthlyTrend.map(m => m.count), color: accentColor, fill: true, pointRadius: 3 }]}
                yMin={0}
              />
            ) : <EmptyChart label="trend" themeColor={accentColor} />}
          </ChartCard>
        </div>}
        {visible.status !== false && <div className="col-lg-5">
          <ChartCard title="Status" subtitle="Distribution" icon="bi-pie-chart-fill" iconColor={accentColor}>
            {statusSlices.length ? (
              <DoughnutChart height={260}
                slices={statusSlices}
                centerLabel={kpis.total || 0}
                centerSubLabel="Total"
                themeColor={accentColor}
              />
            ) : <EmptyChart label="status" themeColor={accentColor} />}
          </ChartCard>
        </div>}
      </div>

      <div className="row g-3 mb-4">
        {visible.college !== false && <div className="col-lg-6">
          <ChartCard title="By College" subtitle="Top 8" icon="bi-building" iconColor={accentColor}>
            {byCollege.length ? (
              <BarChart
                height={Math.max(180, byCollege.length * 28)}
                labels={byCollege.map(c => c.collegeName.length > 22 ? c.collegeName.slice(0, 21) + '…' : c.collegeName)}
                datasets={[{ label: 'Count', data: byCollege.map(c => c.count), color: accentColor }]}
                themeColor={accentColor}
              />
            ) : <EmptyChart label="colleges" themeColor={accentColor} />}
          </ChartCard>
        </div>}
        {visible.course !== false && <div className="col-lg-6">
          <ChartCard title="By Course" subtitle="Top 8" icon="bi-journal-bookmark-fill" iconColor={accentColor}>
            {byCourse.length ? (
              <BarChart
                height={Math.max(180, byCourse.length * 28)}
                labels={byCourse.map(c => c.courseName.length > 22 ? c.courseName.slice(0, 21) + '…' : c.courseName)}
                datasets={[{ label: 'Count', data: byCourse.map(c => c.count), color: accentColor }]}
                themeColor={accentColor}
              />
            ) : <EmptyChart label="courses" themeColor={accentColor} />}
          </ChartCard>
        </div>}
      </div>

      {visible.type !== false && byType.length > 0 && (
        <div className="row g-3 mb-4">
          <div className="col-lg-5">
            <ChartCard title="Individual vs Group" icon="bi-people-fill" iconColor={accentColor}>
              <DoughnutChart height={220}
                slices={byType.map(t => ({ label: t.type === 'individual' ? 'Individual' : 'Group', value: t.count, color: t.type === 'individual' ? '#3b82f6' : '#8b5cf6' }))}
                centerLabel={byType.reduce((s, t) => s + t.count, 0)}
                centerSubLabel="Total"
                themeColor={accentColor}
              />
            </ChartCard>
          </div>
        </div>
      )}
      <AnalyticsSettingsModal
        show={showSettings}
        charts={chartOptions}
        visibility={visible}
        onToggle={controls.toggleChart}
        onReset={controls.reset}
        onClose={() => setShowSettings(false)}
      />
      <AnalyticsInfoModal
        show={showInfo}
        title="Internship / Project Analytics Guide"
        lines={[
          'Use the same date controls here as in the other training analytics tabs.',
          'College, course, and type charts now follow the selected range.',
          'Chart visibility is stored separately for each internship and project analytics screen.',
        ]}
        onClose={() => setShowInfo(false)}
      />
    </div>
  );
};

// ─── Single resource panel (table + analytics toggle) ───────────────────────
// Encapsulates the state + handlers for one resource (either
// enquiry or joined). Used twice on the page — once per tab.
const ResourcePanel = ({ config, isJoined, statuses, accentColor, navigate }) => {
  const [records, setRecords]           = useState([]);
  const [stats, setStats]               = useState(null);
  const [courses, setCourses]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [formError, setFormError]       = useState('');

  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [search, setSearch]             = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType]     = useState('');

  const [showForm, setShowForm]         = useState(false);
  const [editTarget, setEditTarget]     = useState(null);
  const [form, setForm]                 = useState({});
  const [showDetails, setShowDetails]   = useState(false);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpTarget, setFollowUpTarget] = useState(null);
  const [followUpForm, setFollowUpForm] = useState({ method: 'call', outcome: 'callback', nextFollowUp: '', conversation: '' });
  const [showPayment, setShowPayment]   = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [paymentForm, setPaymentForm]   = useState({ amount: '', method: 'cash', paymentDate: '', transactionReference: '', remarks: '' });

  // View toggle: table | analytics (default = table)
  const [view, setView] = useState(() => readViewPreference(`internship-project-${config.resource}`, 'table'));

  // Alert toast
  const [alert, setAlert] = useState({ show: false, msg: '', type: 'success' });
  const showAlert = (msg, type = 'success') => setAlert({ show: true, msg, type });

  // Initial load: courses dropdown options + KPI stats
  useEffect(() => {
    api.get('/training/courses', { params: { limit: 500 } })
      .then(r => {
        const list = r.data?.records || r.data?.data || [];
        setCourses(Array.isArray(list) ? list : []);
      }).catch(() => {});
    api.get(`${config.apiBase}/stats`)
      .then(r => setStats(r.data?.data || null))
      .catch(() => {});
  }, [config.apiBase]);

  // List fetch — debounced on search so we don't hammer the server
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search)     params.search     = search;
      if (filterStatus) params.status  = filterStatus;
      if (filterType) params.type      = filterType;
      const r = await api.get(config.apiBase, { params });
      setRecords(r.data?.records || r.data?.data || []);
      setTotalPages(r.data?.totalPages || 1);
    } catch (_) { setRecords([]); }
    finally { setLoading(false); }
  }, [config.apiBase, page, search, filterStatus, filterType]);

  useEffect(() => {
    const t = setTimeout(fetchRecords, 250);
    return () => clearTimeout(t);
  }, [fetchRecords]);

  // Form helpers
  const blankForm = () => ({
    studentName: '', enquiryDate: new Date().toISOString().split('T')[0], collegeName: '', phoneNumber: '',
    type: 'individual', membersCount: 1,
    amountPerPerson: 0, paidAmount: 0,
    courseId: '', courseName: '', courseOther: false, otherCourse: '',
    notes: '', nextFollowUp: '',
    joiningDate: '', completedDate: '', status: 'ongoing',
    certificateGiven: false, certificateNumber: '',
  });

  const openCreate = () => {
    setForm({ ...blankForm(), joiningDate: new Date().toISOString().split('T')[0] });
    setEditTarget(null);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (r) => {
    setShowDetails(false);
    setForm({
      studentName: r.studentName || '',
      enquiryDate: r.enquiryDate || '',
      collegeName: r.collegeName || '',
      phoneNumber: r.phoneNumber || '',
      type: r.type || 'individual',
      membersCount: r.membersCount || 1,
      amountPerPerson: r.amountPerPerson || 0,
      paidAmount: r.paidAmount || 0,
      courseId: r.courseId?._id || r.courseId || '',
      courseName: r.courseName || '',
      courseOther: !r.courseId && !!r.otherCourse,
      otherCourse: r.otherCourse || '',
      notes: r.notes || '',
      nextFollowUp: r.nextFollowUp ? new Date(r.nextFollowUp).toISOString().split('T')[0] : '',
      joiningDate: r.joiningDate || '',
      completedDate: r.completedDate || '',
      status: r.status || 'ongoing',
      certificateGiven: !!r.certificateGiven,
      certificateNumber: r.certificateNumber || '',
    });
    setEditTarget(r);
    setFormError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.studentName || !form.studentName.trim()) {
      setFormError('Student name is required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        studentName: form.studentName.trim(),
        enquiryDate: form.enquiryDate || null,
        collegeName: form.collegeName?.trim() || '',
        phoneNumber: form.phoneNumber?.trim() || '',
        type: form.type,
        membersCount: form.membersCount,
        amountPerPerson: form.amountPerPerson,
        courseId: form.courseOther ? null : (form.courseId || null),
        courseName: form.courseName || '',
        otherCourse: form.courseOther ? (form.otherCourse || '') : '',
        notes: form.notes || '',
        nextFollowUp: form.nextFollowUp || null,
      };
      if (isJoined) {
        Object.assign(payload, {
          joiningDate: form.joiningDate || null,
          completedDate: form.completedDate || null,
          status: form.status,
          certificateGiven: !!form.certificateGiven,
          certificateNumber: form.certificateNumber || '',
        });
      }
      let r;
      if (editTarget) r = await api.put(`${config.apiBase}/${editTarget._id}`, payload);
      else            r = await api.post(config.apiBase, payload);
      if (r.data?.success !== false) {
        showAlert(editTarget ? 'Updated successfully.' : `${config.singular} created.`);
        setShowForm(false);
        fetchRecords();
        // Refresh stats
        api.get(`${config.apiBase}/stats`).then(rr => setStats(rr.data?.data || null)).catch(() => {});
      } else {
        setFormError(r.data?.message || 'Save failed.');
      }
} catch (e) {
      console.error('[convert catch] error:', e.message, e.response?.status, e.response?.data);
      showAlert(e.response?.data?.message || 'Convert failed.', 'danger');
    }
    setSaving(false);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete ${config.singular.toLowerCase()} for ${r.studentName}?`)) return;
    try {
      await api.delete(`${config.apiBase}/${r._id}`);
      showAlert('Deleted.');
      fetchRecords();
      api.get(`${config.apiBase}/stats`).then(rr => setStats(rr.data?.data || null)).catch(() => {});
    } catch (e) {
      showAlert(e.response?.data?.message || 'Delete failed.', 'danger');
    }
  };

  const handleConvert = async (r) => {
    if (!r?._id) return;
    if (r.status === 'converted') {
      showAlert('This enquiry is already added to joined tab.', 'info');
      return;
    }
    setSaving(true);
    try {
      await api.post(`${config.apiBase}/${r._id}/convert`, {
        joiningDate: new Date().toISOString().split('T')[0],
      });
      showAlert('Added to joined tab successfully.');
      fetchRecords();
      api.get(`${config.apiBase}/stats`).then(rr => setStats(rr.data?.data || null)).catch(() => {});
    } catch (e) {
      showAlert(e.response?.data?.message || 'Convert failed.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (r, status) => {
    if (!isJoined && status === 'converted' && r.status !== 'converted') {
      return handleConvert(r);
    }
    try {
      await api.patch(`${config.apiBase}/${r._id}/status`, { status });
      showAlert('Status updated.');
      fetchRecords();
      api.get(`${config.apiBase}/stats`).then(rr => setStats(rr.data?.data || null)).catch(() => {});
    } catch (e) {
      showAlert(e.response?.data?.message || 'Update failed.', 'danger');
    }
  };

  const openDetails = async (r) => {
    try {
      const res = await api.get(`${config.apiBase}/${r._id}`);
      setDetailsTarget(res.data?.data || r);
      setShowDetails(true);
    } catch (e) {
      showAlert(e.response?.data?.message || 'Unable to load details.', 'danger');
    }
  };

  const openFollowUp = async (r) => {
    try {
      const res = await api.get(`${config.apiBase}/${r._id}`);
      const detail = res.data?.data || r;
      setFollowUpTarget(detail);
      setFollowUpForm({
        method: 'call',
        outcome: 'callback',
        nextFollowUp: detail?.nextFollowUp ? new Date(detail.nextFollowUp).toISOString().split('T')[0] : '',
        conversation: '',
      });
      setShowFollowUp(true);
    } catch (e) {
      showAlert(e.response?.data?.message || 'Unable to open follow-up', 'danger');
    }
  };

  const handleFollowUpSave = async () => {
    if (!followUpTarget?._id) return;
    if (!String(followUpForm.conversation || '').trim()) {
      return showAlert('Follow-up conversation is required.', 'warning');
    }
    setSaving(true);
    try {
      const res = await api.post(`${config.apiBase}/${followUpTarget._id}/follow-up`, {
        method: followUpForm.method,
        outcome: followUpForm.outcome,
        nextFollowUp: followUpForm.nextFollowUp || null,
        conversation: followUpForm.conversation,
      });
      setFollowUpTarget(res.data?.data || followUpTarget);
      setShowFollowUp(false);
      showAlert('Follow-up saved.');
      fetchRecords();
      api.get(`${config.apiBase}/stats`).then(rr => setStats(rr.data?.data || null)).catch(() => {});
    } catch (e) {
      showAlert(e.response?.data?.message || 'Follow-up save failed.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleCertificateToggle = async (r) => {
    try {
      await api.patch(`${config.apiBase}/${r._id}/certificate`, {
        certificateGiven: !r.certificateGiven,
        certificateNumber: r.certificateNumber,
      });
      showAlert(!r.certificateGiven ? 'Certificate marked as issued.' : 'Certificate revoked.');
      fetchRecords();
      api.get(`${config.apiBase}/stats`).then(rr => setStats(rr.data?.data || null)).catch(() => {});
    } catch (e) {
      showAlert(e.response?.data?.message || 'Update failed.', 'danger');
    }
  };

  const openPayment = (r) => {
    setPaymentTarget(r);
    setPaymentForm({
      amount: r.balanceAmount || '',
      method: 'cash',
      paymentDate: new Date().toISOString().split('T')[0],
      transactionReference: '',
      remarks: '',
    });
    setShowPayment(true);
  };

  const handlePaymentSave = async () => {
    if (!paymentTarget) return;
    setSaving(true);
    try {
      await api.post(`${config.apiBase}/${paymentTarget._id}/payments`, paymentForm);
      showAlert('Payment recorded.');
      setShowPayment(false);
      fetchRecords();
      api.get(`${config.apiBase}/stats`).then(rr => setStats(rr.data?.data || null)).catch(() => {});
    } catch (e) {
      showAlert(e.response?.data?.message || 'Payment save failed.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Choose which config to pass to the form/table
  const formConfig = { singular: config.singular, typeLabel: config.typeLabel };

  return (
    <div>
      <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={() => setAlert(p => ({ ...p, show: false }))} />
      {/* Top-right: view toggle (management only) */}
      <div className="d-flex justify-content-end mb-2">
        <ViewToggle
          storageKey={`internship-project-${config.resource}`}
          value={view}
          onChange={setView}
          options={isJoined ? [
            { value: 'table', label: 'Table View', icon: 'bi-table' },
            { value: 'analytics', label: 'Analytics', icon: 'bi-bar-chart-line-fill' },
            { value: 'collections', label: 'Collections', icon: 'bi-cash-stack' },
          ] : undefined}
        />
      </div>

      {/* KPI strip */}
      {stats && (
        <div className="row g-2 mb-3">
          <div className="col-6 col-md-2"><StatCard title="Total" value={stats.total} icon="bi-collection" color="primary" /></div>
          {isJoined ? (
            <>
              <div className="col-6 col-md-2"><StatCard title="Ongoing"      value={stats.ongoing || 0}        icon="bi-play-circle"   color="info" /></div>
              <div className="col-6 col-md-2"><StatCard title="Completed"    value={stats.completed || 0}      icon="bi-check-circle"  color="success" /></div>
              <div className="col-6 col-md-2"><StatCard title="Cancelled"    value={stats.cancelled || 0}      icon="bi-x-circle"      color="danger" /></div>
              <div className="col-6 col-md-2"><StatCard title="Certificates" value={stats.certificatesIssued || 0} icon="bi-patch-check" color="warning" /></div>
              <div className="col-6 col-md-2"><StatCard title="Collected"    value={fmtINR(stats.totalCollected || 0)} icon="bi-cash-stack" color="success" /></div>
              <div className="col-6 col-md-2"><StatCard title="Balance"      value={fmtINR(stats.totalBalance || 0)} icon="bi-hourglass-split" color="warning" /></div>
            </>
          ) : (
            <>
              <div className="col-6 col-md-2"><StatCard title="New"        value={stats.new || 0}       icon="bi-stars"         color="info" /></div>
              <div className="col-6 col-md-2"><StatCard title="Contacted"  value={stats.contacted || 0} icon="bi-telephone"     color="primary" /></div>
              <div className="col-6 col-md-2"><StatCard title="Follow Up"  value={stats.followUp || 0}  icon="bi-arrow-repeat"  color="warning" /></div>
              <div className="col-6 col-md-2"><StatCard title="Converted"  value={stats.converted || 0} icon="bi-check-circle"  color="success" /></div>
              <div className="col-6 col-md-2"><StatCard title="Lost"       value={stats.lost || 0}      icon="bi-x-circle"      color="secondary" /></div>
            </>
          )}
        </div>
      )}

      {/* Analytics OR table */}
      {view === 'analytics' ? (
        <ResourceAnalytics
          analyticsBase={config.analyticsBase}
          accentColor={accentColor}
          isJoined={isJoined}
          navigateTo={() => navigate && navigate(config.pagePath || '')}
        />
      ) : view === 'collections' && isJoined ? (
        <JoinedResourceCollectionsAnalytics
          statsPath={`${config.apiBase}/stats/collections`}
          submitPath={`${config.apiBase}/stats/collections/submit`}
          titleLabel={config.singular}
        />
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0 text-muted">
              <i className="bi bi-list-ul me-2" />
              {records.length} {config.singular}{records.length === 1 ? '' : 's'}
            </h6>
            <button className="btn btn-primary btn-sm" onClick={openCreate}>
              <i className="bi bi-plus-lg me-1" />New {config.singular}
            </button>
          </div>

          <ResourceTable
            records={records}
            totalPages={totalPages}
            page={page}
            loading={loading}
            search={search}
            filterStatus={filterStatus}
            filterType={filterType}
            statuses={statuses}
            types={TYPES}
            hasMembers={true}
            hasAmount={true}
            hasCertificate={true}
            config={config}
            canWrite={true}
            canAdmin={true}
            isJoined={isJoined}
            onAddPayment={openPayment}
            onPageChange={setPage}
            onSearchChange={setSearch}
            onFilterStatusChange={setFilterStatus}
            onFilterTypeChange={setFilterType}
            onAdd={openCreate}
            onEdit={openEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onCertificateToggle={handleCertificateToggle}
            onConvert={handleConvert}
            onFollowUp={openFollowUp}
            onViewDetails={openDetails}
          />
        </>
      )}

      <ResourceForm
        show={showForm}
        onClose={() => setShowForm(false)}
        form={form}
        setForm={setForm}
        isEdit={!!editTarget}
        isJoined={isJoined}
        courses={courses}
        saving={saving}
        error={formError}
        onSubmit={handleSave}
        config={formConfig}
      />

      <FollowUpModal
        show={showFollowUp}
        onClose={() => setShowFollowUp(false)}
        record={followUpTarget}
        form={followUpForm}
        setForm={setFollowUpForm}
        saving={saving}
        onSubmit={handleFollowUpSave}
      />

      <ResourceDetailsModal
        show={showDetails}
        onClose={() => setShowDetails(false)}
        record={detailsTarget}
        isJoined={isJoined}
        onEdit={openEdit}
      />

      <BSModal
        show={showPayment}
        onClose={() => setShowPayment(false)}
        title={`Record Payment${paymentTarget?.studentName ? ` - ${paymentTarget.studentName}` : ''}`}
        footer={
          <>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowPayment(false)} disabled={saving}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handlePaymentSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving...</> : 'Save Payment'}
            </button>
          </>
        }
      >
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Amount</label>
            <input type="number" min="0" className="form-control form-control-sm" value={paymentForm.amount || ''} onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Method</label>
            <select className="form-select form-select-sm" value={paymentForm.method} onChange={e => setPaymentForm(p => ({ ...p, method: e.target.value }))}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="card">Card</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="dd">Demand Draft</option>
              <option value="online">Online</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Payment Date</label>
            <input type="date" className="form-control form-control-sm" value={paymentForm.paymentDate || ''} onChange={e => setPaymentForm(p => ({ ...p, paymentDate: e.target.value }))} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Transaction Reference</label>
            <input className="form-control form-control-sm" value={paymentForm.transactionReference || ''} onChange={e => setPaymentForm(p => ({ ...p, transactionReference: e.target.value }))} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Remarks</label>
            <input className="form-control form-control-sm" value={paymentForm.remarks || ''} onChange={e => setPaymentForm(p => ({ ...p, remarks: e.target.value }))} />
          </div>
          <div className="col-12">
            <div className="small text-muted">
              Total: {fmtINR(paymentTarget?.totalAmount || 0)} | Paid: {fmtINR(paymentTarget?.paidAmount || 0)} | Balance: {fmtINR(paymentTarget?.balanceAmount || 0)}
            </div>
          </div>
        </div>
      </BSModal>
    </div>
  );
};

// ─── Page wrapper — top-level tabs (Internship | Project) ───────────────────
const InternshipCertificatesPanel = () => {
  const defaultLayoutSettings = {
    pagePaddingTop: 48,
    pagePaddingSide: 56,
    pagePaddingBottom: 54,
    titleTopSpacing: 34,
    bodyTopSpacing: 42,
    footerTopSpacing: 18,
    issueDateAlign: 'left',
    titleAlign: 'center',
    bodyAlign: 'justify',
    footerAlign: 'space-between',
    signatoryLayout: 'both',
    issueDateSize: 15,
    subtitleSize: 16,
    titleSize: 28,
    firstParagraphSize: 18,
    paragraphSize: 15,
    signatoryTitleSize: 15,
    signatoryNameSize: 17,
    signatoryGap: 72,
    titleColor: '#111827',
    bodyColor: '#374151',
    footerColor: '#111827',
  };
  const [templates, setTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState('');
  const [joinedOptions, setJoinedOptions] = useState([]);
  const [selectedJoinedId, setSelectedJoinedId] = useState('');
  const [records, setRecords] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [bulkFailures, setBulkFailures] = useState([]);
  const [recordTab, setRecordTab] = useState('yet_to_print');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [templateDraft, setTemplateDraft] = useState({
    certificateTitleTemplate: '',
    subtitleTemplate: '',
    contentTemplate: '',
    signatoryLeftTitleTemplate: '',
    signatoryLeftNameTemplate: '',
    signatoryRightTitleTemplate: '',
    signatoryRightNameTemplate: '',
    layoutSettings: defaultLayoutSettings,
  });
  const [alert, setAlert] = useState({ show: false, msg: '', type: 'success' });
  const showAlert = (msg, type = 'success') => setAlert({ show: true, msg, type });
  const previewColumns = (previewRows[0]?.availableFields || []).filter((field) => !['_id', '__v'].includes(field));
  const filteredRecords = records.filter((row) => (row.deliveryStatus || 'yet_to_print') === recordTab);
  const formatPreviewValue = (field, value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (value instanceof Date) return fmtDate(value);
    if (typeof value === 'string' && /date/i.test(field) && !Number.isNaN(new Date(value).getTime())) {
      return fmtDate(value);
    }
    return String(value);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [templatesRes, joinedRes, recordsRes] = await Promise.all([
        api.get('/training/internships/certificates/templates'),
        api.get('/training/internships/certificates/joined-options'),
        api.get('/training/internships/certificates/records', { params: { limit: 50, search } }),
      ]);
      const nextTemplates = templatesRes.data?.records || templatesRes.data?.data || [];
      setTemplates(nextTemplates);
      const chosenTemplateId = activeTemplateId || nextTemplates.find(t => t.isDefault)?._id || nextTemplates[0]?._id || '';
      setActiveTemplateId(chosenTemplateId);
      const chosenTemplate = nextTemplates.find(t => t._id === chosenTemplateId) || nextTemplates[0] || null;
      setTemplateDraft({
        certificateTitleTemplate: chosenTemplate?.certificateTitleTemplate ?? 'Internship Certificate',
        subtitleTemplate: chosenTemplate?.subtitleTemplate ?? 'This is to certify that',
        contentTemplate: chosenTemplate?.contentTemplate ?? 'This is to certify that {{studentName}} from {{collegeName}} has successfully completed the internship in {{courseName}} during the period from {{joiningDate}} to {{completedDate}}.',
        signatoryLeftTitleTemplate: chosenTemplate?.signatoryLeftTitleTemplate ?? '',
        signatoryLeftNameTemplate: chosenTemplate?.signatoryLeftNameTemplate ?? '',
        signatoryRightTitleTemplate: chosenTemplate?.signatoryRightTitleTemplate ?? 'Authorized Signatory',
        signatoryRightNameTemplate: chosenTemplate?.signatoryRightNameTemplate ?? '{{tenantName}}',
        layoutSettings: { ...defaultLayoutSettings, ...(chosenTemplate?.layoutSettings || {}) },
      });
      setJoinedOptions(joinedRes.data?.records || joinedRes.data?.data || []);
      setRecords(recordsRes.data?.records || []);
    } catch (e) {
      showAlert(e.response?.data?.message || 'Failed to load internship certificates', 'danger');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const chosenTemplate = templates.find(t => t._id === activeTemplateId);
    if (!chosenTemplate) return;
    setTemplateDraft({
      certificateTitleTemplate: chosenTemplate.certificateTitleTemplate ?? 'Internship Certificate',
      subtitleTemplate: chosenTemplate.subtitleTemplate ?? 'This is to certify that',
      contentTemplate: chosenTemplate.contentTemplate ?? '',
      signatoryLeftTitleTemplate: chosenTemplate.signatoryLeftTitleTemplate ?? '',
      signatoryLeftNameTemplate: chosenTemplate.signatoryLeftNameTemplate ?? '',
      signatoryRightTitleTemplate: chosenTemplate.signatoryRightTitleTemplate ?? 'Authorized Signatory',
      signatoryRightNameTemplate: chosenTemplate.signatoryRightNameTemplate ?? '{{tenantName}}',
      layoutSettings: { ...defaultLayoutSettings, ...(chosenTemplate.layoutSettings || {}) },
    });
  }, [activeTemplateId, templates]);

  useEffect(() => { load(); }, [load]);

  const handleTemplateUpload = async (templateId, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/training/internships/certificates/templates/${templateId}/upload-bg`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showAlert('Letterhead uploaded');
      load();
    } catch (e) {
      showAlert(e.response?.data?.message || 'Upload failed', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const handleIssueSingle = async () => {
    if (!activeTemplateId) return showAlert('Select a template', 'danger');
    if (!selectedJoinedId) return showAlert('Select a joined internship record', 'danger');
    setBusy(true);
    try {
      await api.post('/training/internships/certificates/issue', {
        templateId: activeTemplateId,
        joinedInternshipId: selectedJoinedId,
      });
      showAlert('Internship certificate issued');
      setSelectedJoinedId('');
      load();
    } catch (e) {
      showAlert(e.response?.data?.message || 'Issue failed', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/training/internships/certificates/import-preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreviewRows(res.data?.data?.rows || []);
      setBulkFailures([]);
      showAlert(`Loaded ${res.data?.data?.importedCount || 0} rows for preview`);
    } catch (e) {
      showAlert(e.response?.data?.message || 'Import preview failed', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const handleBulkIssue = async () => {
    if (!activeTemplateId) return showAlert('Select a template', 'danger');
    if (!previewRows.length) return showAlert('Upload an Excel file first', 'danger');
    setBusy(true);
    try {
      const res = await api.post('/training/internships/certificates/bulk-issue', {
        templateId: activeTemplateId,
        rows: previewRows,
      });
      const data = res.data?.data || {};
      setBulkFailures(data.failed || []);
      showAlert(
        data.failedCount
          ? `Issued ${data.issuedCount || 0}, failed ${data.failedCount || 0}. Check failed rows below.`
          : `Issued ${data.issuedCount || 0}, failed 0`,
        data.failedCount ? 'warning' : 'success'
      );
      if (!data.failedCount) {
        setPreviewRows([]);
      }
      load();
    } catch (e) {
      showAlert(e.response?.data?.message || 'Bulk issue failed', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveTemplateContent = async () => {
    if (!activeTemplateId) return showAlert('Select a template', 'danger');
    setBusy(true);
    try {
      await api.put(`/training/internships/certificates/templates/${activeTemplateId}`, templateDraft);
      showAlert('Certificate content template updated');
      load();
    } catch (e) {
      showAlert(e.response?.data?.message || 'Template save failed', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const handleOpenRecord = async (recordId) => {
    try {
      const res = await api.get(`/training/internships/certificates/records/${recordId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      showAlert(e.response?.data?.message || 'Unable to open certificate PDF', 'danger');
    }
  };

  const handlePrintRecord = async (recordId) => {
    try {
      const res = await api.get(`/training/internships/certificates/records/${recordId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.focus();
          printWindow.print();
        }, { once: true });
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      showAlert(e.response?.data?.message || 'Unable to print certificate PDF', 'danger');
    }
  };

  const handleEditRecordTemplate = (row) => {
    const nextTemplateId = row?.templateId?._id || row?.templateId;
    if (nextTemplateId) {
      setActiveTemplateId(String(nextTemplateId));
    }
    document.getElementById('internship-template-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDeleteRecord = async (recordId) => {
    if (!recordId || !window.confirm('Delete this certificate record?')) return;
    setBusy(true);
    try {
      await api.delete(`/training/internships/certificates/records/${recordId}`);
      showAlert('Internship certificate deleted');
      load();
    } catch (e) {
      showAlert(e.response?.data?.message || 'Unable to delete certificate', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const handleDeliveryStatus = async (recordId, deliveryStatus) => {
    setBusy(true);
    try {
      await api.patch(`/training/internships/certificates/records/${recordId}/delivery`, { deliveryStatus });
      showAlert(deliveryStatus === 'provided' ? 'Certificate moved to Provided Certificate' : 'Certificate moved to Yet to Print');
      load();
    } catch (e) {
      showAlert(e.response?.data?.message || 'Unable to update certificate status', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const handlePrintAll = async () => {
    const rows = filteredRecords.filter((row) => (row.deliveryStatus || 'yet_to_print') === recordTab);
    if (!rows.length) return showAlert('No certificates available in this tab', 'warning');
    try {
      const res = await api.post('/training/internships/certificates/records/print-batch', {
        recordIds: rows.map((row) => row._id),
      }, { responseType: 'text' });
      const printWindow = window.open('', '_blank', 'noopener,noreferrer');
      if (!printWindow) return showAlert('Popup blocked while opening print window', 'danger');
      printWindow.document.open();
      printWindow.document.write(res.data);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        try { printWindow.print(); } catch (_) {}
      }, 600);
    } catch (e) {
      showAlert(e.response?.data?.message || 'Unable to print certificates', 'danger');
    }
  };

  return (
    <div>
      <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={() => setAlert(p => ({ ...p, show: false }))} />

      <div className="row g-3 mb-4">
        {(templates || []).map((tpl) => (
          <div key={tpl._id} className="col-md-6">
            <div className={`card h-100 shadow-sm ${activeTemplateId === tpl._id ? 'border-primary' : ''}`}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <div className="fw-semibold">{tpl.name}</div>
                    <div className="small text-muted">{tpl.description || 'Internship letterhead template'}</div>
                  </div>
                  {tpl.isDefault && <span className="badge bg-primary">Default</span>}
                </div>
                <div className="small text-muted mb-3">
                  {tpl.backgroundAsset ? `Letterhead: ${tpl.backgroundAsset.split('/').pop()}` : 'No letterhead uploaded yet'}
                </div>
                {/\.(pdf)$/i.test(tpl.backgroundAsset || '') && (
                  <div className="small text-warning mb-3">
                    PDF letterheads are less reliable for live preview. PNG or JPG gives the most accurate result.
                  </div>
                )}
                <div className="small text-muted mb-3">
                  Canvas: {tpl.widthPx || 1123} x {tpl.heightPx || 794}
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <button className={`btn btn-sm ${activeTemplateId === tpl._id ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setActiveTemplateId(tpl._id)}>
                    Use Template
                  </button>
                  <label className="btn btn-sm btn-outline-secondary mb-0">
                    Upload Letterhead
                    <input type="file" hidden accept="image/png,image/jpeg,image/webp,image/svg+xml,application/pdf,.doc,.docx,.dotx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.wordprocessingml.template" onChange={e => handleTemplateUpload(tpl._id, e.target.files?.[0])} />
                  </label>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card shadow-sm mb-4" id="internship-template-editor">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div className="fw-semibold">Certificate Content Template</div>
              <div className="small text-muted">Use placeholders like <code>{'{{studentName}}'}</code>, <code>{'{{collegeName}}'}</code>, <code>{'{{courseName}}'}</code>, <code>{'{{joiningDate}}'}</code>, <code>{'{{completedDate}}'}</code>, <code>{'{{issueDate}}'}</code>, <code>{'{{certificateNumber}}'}</code>.</div>
            </div>
            <button className="btn btn-sm btn-primary" onClick={handleSaveTemplateContent} disabled={busy || !activeTemplateId}>
              {busy ? 'Saving...' : 'Save Content'}
            </button>
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small">Title Template</label>
              <input className="form-control form-control-sm" value={templateDraft.certificateTitleTemplate} onChange={e => setTemplateDraft(p => ({ ...p, certificateTitleTemplate: e.target.value }))} />
            </div>
            <div className="col-md-6">
              <label className="form-label small">Subtitle Template</label>
              <input className="form-control form-control-sm" value={templateDraft.subtitleTemplate} onChange={e => setTemplateDraft(p => ({ ...p, subtitleTemplate: e.target.value }))} />
            </div>
            <div className="col-md-3">
              <label className="form-label small">Left Signatory Title</label>
              <input className="form-control form-control-sm" value={templateDraft.signatoryLeftTitleTemplate || ''} onChange={e => setTemplateDraft(p => ({ ...p, signatoryLeftTitleTemplate: e.target.value }))} placeholder="e.g. Internal Guide" />
            </div>
            <div className="col-md-3">
              <label className="form-label small">Left Signatory Name</label>
              <input className="form-control form-control-sm" value={templateDraft.signatoryLeftNameTemplate || ''} onChange={e => setTemplateDraft(p => ({ ...p, signatoryLeftNameTemplate: e.target.value }))} placeholder="e.g. {{guideName}}" />
            </div>
            <div className="col-md-3">
              <label className="form-label small">Right Signatory Title</label>
              <input className="form-control form-control-sm" value={templateDraft.signatoryRightTitleTemplate || ''} onChange={e => setTemplateDraft(p => ({ ...p, signatoryRightTitleTemplate: e.target.value }))} placeholder="Authorized Signatory" />
            </div>
            <div className="col-md-3">
              <label className="form-label small">Right Signatory Name</label>
              <input className="form-control form-control-sm" value={templateDraft.signatoryRightNameTemplate || ''} onChange={e => setTemplateDraft(p => ({ ...p, signatoryRightNameTemplate: e.target.value }))} placeholder="{{tenantName}}" />
            </div>
            <div className="col-12">
              <label className="form-label small">Content Template</label>
              <textarea className="form-control form-control-sm font-monospace" rows="5" value={templateDraft.contentTemplate} onChange={e => setTemplateDraft(p => ({ ...p, contentTemplate: e.target.value }))} />
            </div>
            <div className="col-12">
              <div className="fw-semibold small mt-2 mb-2">Layout Controls</div>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Top Padding</label>
              <input type="number" className="form-control form-control-sm" value={templateDraft.layoutSettings?.pagePaddingTop || 0} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, pagePaddingTop: Number(e.target.value) || 0 } }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Side Padding</label>
              <input type="number" className="form-control form-control-sm" value={templateDraft.layoutSettings?.pagePaddingSide || 0} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, pagePaddingSide: Number(e.target.value) || 0 } }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Issue Date Size</label>
              <input type="number" className="form-control form-control-sm" value={templateDraft.layoutSettings?.issueDateSize || 0} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, issueDateSize: Number(e.target.value) || 0 } }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Title Size</label>
              <input type="number" className="form-control form-control-sm" value={templateDraft.layoutSettings?.titleSize || 0} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, titleSize: Number(e.target.value) || 0 } }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Subtitle Size</label>
              <input type="number" className="form-control form-control-sm" value={templateDraft.layoutSettings?.subtitleSize || 0} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, subtitleSize: Number(e.target.value) || 0 } }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Body Size</label>
              <input type="number" className="form-control form-control-sm" value={templateDraft.layoutSettings?.paragraphSize || 0} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, paragraphSize: Number(e.target.value) || 0 } }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Issue Date Align</label>
              <select className="form-select form-select-sm" value={templateDraft.layoutSettings?.issueDateAlign || 'left'} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, issueDateAlign: e.target.value } }))}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Title Align</label>
              <select className="form-select form-select-sm" value={templateDraft.layoutSettings?.titleAlign || 'center'} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, titleAlign: e.target.value } }))}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Body Align</label>
              <select className="form-select form-select-sm" value={templateDraft.layoutSettings?.bodyAlign || 'justify'} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, bodyAlign: e.target.value } }))}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
                <option value="justify">Justify</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Footer Align</label>
              <select className="form-select form-select-sm" value={templateDraft.layoutSettings?.footerAlign || 'space-between'} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, footerAlign: e.target.value } }))}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
                <option value="space-between">Split</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Signatory View</label>
              <select className="form-select form-select-sm" value={templateDraft.layoutSettings?.signatoryLayout || 'both'} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, signatoryLayout: e.target.value } }))}>
                <option value="left">Left Only</option>
                <option value="right">Right Only</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Signatory Gap</label>
              <input type="number" className="form-control form-control-sm" value={templateDraft.layoutSettings?.signatoryGap || 0} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, signatoryGap: Number(e.target.value) || 0 } }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Signatory Title Size</label>
              <input type="number" className="form-control form-control-sm" value={templateDraft.layoutSettings?.signatoryTitleSize || 0} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, signatoryTitleSize: Number(e.target.value) || 0 } }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Signatory Name Size</label>
              <input type="number" className="form-control form-control-sm" value={templateDraft.layoutSettings?.signatoryNameSize || 0} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, signatoryNameSize: Number(e.target.value) || 0 } }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Title Color</label>
              <input type="color" className="form-control form-control-sm form-control-color" value={templateDraft.layoutSettings?.titleColor || '#111827'} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, titleColor: e.target.value } }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Body Color</label>
              <input type="color" className="form-control form-control-sm form-control-color" value={templateDraft.layoutSettings?.bodyColor || '#374151'} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, bodyColor: e.target.value } }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Footer Color</label>
              <input type="color" className="form-control form-control-sm form-control-color" value={templateDraft.layoutSettings?.footerColor || '#111827'} onChange={e => setTemplateDraft(p => ({ ...p, layoutSettings: { ...p.layoutSettings, footerColor: e.target.value } }))} />
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-5">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="fw-semibold mb-2">Issue From Joined Internship</div>
              <div className="small text-muted mb-3">Use the fixed company letterhead and generate one certificate directly from an existing joined record.</div>
              <div className="mb-3">
                <label className="form-label small">Joined Record</label>
                <select className="form-select form-select-sm" value={selectedJoinedId} onChange={e => setSelectedJoinedId(e.target.value)}>
                  <option value="">Select joined internship</option>
                  {joinedOptions.map((row) => (
                    <option key={row._id} value={row._id}>
                      {row.joinedNumber} - {row.studentName} - {row.courseName || 'Internship'}
                    </option>
                  ))}
                </select>
              </div>
              <button className="btn btn-primary btn-sm" onClick={handleIssueSingle} disabled={busy || !selectedJoinedId || !activeTemplateId}>
                {busy ? 'Processing...' : 'Issue Certificate'}
              </button>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="fw-semibold mb-2">Bulk Issue From Excel</div>
              <div className="small text-muted mb-3">
                Upload `.xlsx`, `.xls`, or `.csv` with columns like `studentName`, `collegeName`, `courseName`, `joiningDate`, `completedDate`, `issueDate`, `joinedNumber`. The content template placeholders will be filled from each Excel row.
              </div>
              <div className="d-flex gap-2 flex-wrap align-items-center mb-3">
                <label className="btn btn-sm btn-outline-secondary mb-0">
                  Upload Excel
                  <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={e => handleImportFile(e.target.files?.[0])} />
                </label>
                <button className="btn btn-sm btn-success" onClick={handleBulkIssue} disabled={busy || !previewRows.length || !activeTemplateId}>
                  {busy ? 'Processing...' : `Generate ${previewRows.length || ''} Certificates`}
                </button>
              </div>

              {previewRows.length > 0 && (
                <div>
                  <div className="small text-muted mb-2">
                    Available field names from Excel:
                    <div className="mt-1 d-flex flex-wrap gap-1">
                      {previewColumns.slice(0, 32).map((field) => (
                        <code key={field}>{field}</code>
                      ))}
                    </div>
                  </div>
                  <div className="table-responsive" style={{ maxHeight: 280 }}>
                    <table className="table table-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          {previewColumns.map((field) => (
                            <th key={field}>{field}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.slice(0, 50).map((row) => (
                          <tr key={row.rowNumber}>
                            <td>{row.rowNumber}</td>
                            {previewColumns.map((field) => (
                              <td key={`${row.rowNumber}-${field}`} className="small">
                                {formatPreviewValue(field, row.mergeData?.[field] ?? row[field])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {bulkFailures.length > 0 && (
                <div className="mt-3">
                  <div className="fw-semibold text-danger mb-2">Failed Rows</div>
                  <div className="table-responsive" style={{ maxHeight: 220 }}>
                    <table className="table table-sm table-bordered align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Row</th>
                          <th>Joined No.</th>
                          <th>Student</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkFailures.map((row, index) => (
                          <tr key={`${row.rowNumber || 'row'}-${index}`}>
                            <td>{row.rowNumber || '-'}</td>
                            <td>{row.joinedNumber || '-'}</td>
                            <td>{row.studentName || '-'}</td>
                            <td className="text-danger">{row.reason || 'Issue failed'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <div className="fw-semibold">Issued Internship Certificates</div>
              <div className="small text-muted">Stored issue history for reprint and tracking</div>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <button className="btn btn-sm btn-outline-secondary" onClick={handlePrintAll} disabled={busy || !filteredRecords.length}>
                <i className="bi bi-printer me-1" />Print All
              </button>
              <div style={{ width: 280 }}>
                <input className="form-control form-control-sm" placeholder="Search student / certificate / internship" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 mb-3">
            <button className={`btn btn-sm ${recordTab === 'yet_to_print' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setRecordTab('yet_to_print')}>
              Certificate Yet to Print ({records.filter((row) => (row.deliveryStatus || 'yet_to_print') === 'yet_to_print').length})
            </button>
            <button className={`btn btn-sm ${recordTab === 'provided' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setRecordTab('provided')}>
              Provided Certificate ({records.filter((row) => (row.deliveryStatus || 'yet_to_print') === 'provided').length})
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Certificate No.</th>
                    <th>Student</th>
                    <th>College</th>
                    <th>Internship</th>
                    <th>Issue Date</th>
                    <th>Template</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.length === 0 ? (
                    <tr><td colSpan="7" className="text-center text-muted py-4">No internship certificates issued yet.</td></tr>
                  ) : filteredRecords.map((row) => (
                    <tr key={row._id}>
                      <td className="font-monospace small">{row.certificateNumber}</td>
                      <td>{row.studentName}</td>
                      <td>{row.collegeName || '-'}</td>
                      <td>{row.courseName || '-'}</td>
                      <td>{fmtDate(row.issueDate)}</td>
                      <td>{row.templateId?.name || '-'}</td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleOpenRecord(row._id)}>
                          <i className="bi bi-download me-1" />Open PDF
                        </button>
                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => handlePrintRecord(row._id)}>
                          <i className="bi bi-printer me-1" />Print
                        </button>
                        <button className="btn btn-sm btn-outline-warning me-2" onClick={() => handleEditRecordTemplate(row)}>
                          <i className="bi bi-pencil-square me-1" />Edit
                        </button>
                        {recordTab === 'yet_to_print' ? (
                          <button className="btn btn-sm btn-outline-success me-2" onClick={() => handleDeliveryStatus(row._id, 'provided')} disabled={busy}>
                            <i className="bi bi-check2-circle me-1" />Issued
                          </button>
                        ) : (
                          <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleDeliveryStatus(row._id, 'yet_to_print')} disabled={busy}>
                            <i className="bi bi-arrow-counterclockwise me-1" />Move Back
                          </button>
                        )}
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteRecord(row._id)} disabled={busy}>
                          <i className="bi bi-trash me-1" />Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InternshipProjectPage = ({ module }) => {
  const navigate = useNavigate();
  const config = MODULE_CONFIG[module];

  // Outer tab state: 'internship' | 'project' OR same? The spec asks
  // for both — but the page path itself is one of /internships or
  // /projects. So when module='internship', the inner tab is just
  // Enquiry / Joined. Project is on a separate page.
  // Inner tab: 'enquiry' | 'joined'
  const [tab, setTab] = useState(() => readViewPreference(`internship-project-tab-${module}`, 'enquiry'));

  return (
    <div className="container-fluid py-3" style={{ maxWidth: '98%' }}>
      <PageHeader
        title={config.title}
        icon={module === 'internship' ? 'bi-briefcase-fill' : 'bi-kanban-fill'}
        subtitle={module === 'internship'
          ? 'Manage internship enquiries, joined records, analytics, and certificate issuing'
          : `Manage ${module} enquiries, joined records & analytics`}
      />

      {/* Inner tabs */}
      <ul className="nav nav-pills mb-3" style={{ gap: 4 }}>
        <li className="nav-item">
          <button
            className={`nav-link ${tab === 'enquiry' ? 'active' : ''}`}
            onClick={() => {
              setTab('enquiry');
              try { localStorage.setItem(`viewToggle:internship-project-tab-${module}`, 'enquiry'); } catch (_) {}
            }}
          >
            <i className="bi bi-chat-square-text me-1" />Enquiry Management
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${tab === 'joined' ? 'active' : ''}`}
            onClick={() => {
              setTab('joined');
              try { localStorage.setItem(`viewToggle:internship-project-tab-${module}`, 'joined'); } catch (_) {}
            }}
          >
            <i className="bi bi-people-fill me-1" />Joined Management
          </button>
        </li>
        {module === 'internship' && (
          <li className="nav-item">
            <button
              className={`nav-link ${tab === 'certificates' ? 'active' : ''}`}
              onClick={() => {
                setTab('certificates');
                try { localStorage.setItem(`viewToggle:internship-project-tab-${module}`, 'certificates'); } catch (_) {}
              }}
            >
              <i className="bi bi-patch-check-fill me-1" />Certificates
            </button>
          </li>
        )}
      </ul>

      {/* Tab content */}
      {tab === 'enquiry' ? (
        <ResourcePanel
          config={config.enq}
          isJoined={false}
          statuses={ENQ_STATUSES}
          accentColor={config.enq.badgeColor}
          navigate={navigate}
        />
      ) : tab === 'joined' ? (
        <ResourcePanel
          config={config.joined}
          isJoined={true}
          statuses={JOINED_STATUSES}
          accentColor={config.joined.badgeColor}
          navigate={navigate}
        />
      ) : module === 'internship' ? (
        <InternshipCertificatesPanel />
      ) : (
        <ResourcePanel
          config={config.joined}
          isJoined={true}
          statuses={JOINED_STATUSES}
          accentColor={config.joined.badgeColor}
          navigate={navigate}
        />
      )}
    </div>
  );
};

// ─── Exports ─────────────────────────────────────────────────────────────────
// Two thin wrappers — one per page. Same component, different `module` prop.
export const InternshipsPage = () => <InternshipProjectPage module="internship" />;
export const ProjectsPage    = () => <InternshipProjectPage module="project" />;

export default InternshipProjectPage;
